import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AppShell, { CartButton } from './AppShell';
import Carousel from './Carousel';
import Reveal from './Reveal';
import useOrderStatus from '../hooks/useOrderStatus';
import { getDeliveryDates, formatCurrency, publicImg, API_BASE } from '../config/api';
import './HomePage.css';

const PILLARS = [
    {
        icon: 'fa-solid fa-mortar-pestle',
        title: 'Sên tay, không chất bảo quản',
        body: 'Nhân thập cẩm, đậu xanh vani, cốm dừa, trà xanh, kem trứng muối — ít ngọt, làm mẻ nhỏ mỗi ngày.',
    },
    {
        icon: 'fa-regular fa-calendar-check',
        title: 'Bạn chọn ngày nhận bánh',
        body: 'Đặt trước tối thiểu 48 giờ. Bánh ra lò và giao đúng ngày, đúng khung giờ bạn hẹn.',
    },
    {
        icon: 'fa-solid fa-gift',
        title: 'Tự chọn vị trong hộp',
        body: 'Mix & match từng chiếc vào hộp 2 hoặc 4 bánh. Tặng kèm vỏ hộp và túi xách lịch sự.',
    },
];

const FEATURED = [
    {
        id: 1,
        kind: 'Bánh nướng',
        name: 'Nướng Thập Cẩm',
        desc: 'Lạp xưởng, hạt dưa, mứt bí giòn bùi.',
        price: 55000,
        image: '/imgs/nuong-thap-cam.jpg',
        tag: 'Bán chạy',
    },
    {
        id: 11,
        kind: 'Bánh dẻo',
        name: 'Dẻo Kem Trứng Muối',
        desc: 'Vỏ hoa bưởi, nhân kem trứng muối béo ngậy.',
        price: 55000,
        image: '/imgs/deo-kem-trung-muoi.jpeg',
        tag: 'Mới',
    },
    {
        id: 5,
        kind: 'Bánh nướng',
        name: 'Nướng Cốm Dừa',
        desc: 'Cốm non mùa thu quyện dừa nạo sợi.',
        price: 48000,
        image: '/imgs/nuong-com-dua.webp',
    },
    {
        id: 3,
        kind: 'Bánh nướng',
        name: 'Nướng Đậu Xanh Vani',
        desc: 'Đậu xanh mịn ít đường, hương vani dịu.',
        price: 45000,
        image: '/imgs/nuong-dau-xanh-vani.jpeg',
    },
];

export default function HomePage() {
    const navigate = useNavigate();
    const dates = getDeliveryDates();
    const { orderStatus, orderEnabled } = useOrderStatus();
    const [showPauseModal, setShowPauseModal] = useState(() => !orderEnabled);
    const [selectedDate, setSelectedDate] = useState(() => {
        const saved = localStorage.getItem('selectedDeliveryDate');
        return dates.some(d => d.iso === saved) ? saved : (dates[0]?.iso || '2026-09-14');
    });
    const [featuredCakes, setFeaturedCakes] = useState(FEATURED);

    // Sync modal visibility when order intake status changes
    useEffect(() => {
        if (!orderEnabled) {
            setShowPauseModal(true);
        } else {
            setShowPauseModal(false);
        }
    }, [orderEnabled]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/product/getProduct`);
                const list = res.data?.data;
                if (alive && Array.isArray(list) && list.length > 0) {
                    setFeaturedCakes(prev => prev.map(feat => {
                        const isDeo = feat.kind === 'Bánh dẻo';
                        const cleanName = feat.name.toLowerCase().replace(/^nướng\s+|^dẻo\s+/i, '').trim();
                        // Khớp chính xác theo ID trước, nếu không có ID mới tìm theo đúng loại bánh (nướng/dẻo) và tên vị
                        const match = list.find(p => p.id === feat.id) || list.find(p => {
                            const pName = (p.name || '').toLowerCase();
                            const pCat = (p.category || '').toLowerCase();
                            const matchesKind = isDeo
                                ? (pCat.includes('dẻo') || pName.includes('dẻo'))
                                : (pCat.includes('nướng') || pName.includes('nướng'));
                            return matchesKind && pName.includes(cleanName);
                        });
                        if (match) {
                            return {
                                ...feat,
                                id: match.id,
                                price: Number(match.price) || feat.price,
                                image: match.imageUrl || feat.image,
                            };
                        }
                        return feat;
                    }));
                }
            } catch (err) {
                // Keep default featured cakes
            }
        })();
        return () => { alive = false; };
    }, []);

    const selected = dates.find((d) => d.iso === selectedDate) || dates[0];

    const goMenuWithDate = () => {
        localStorage.setItem('selectedDeliveryDate', selectedDate);
        navigate(`/menu?deliveryDate=${selectedDate}`);
    };

    return (
        <AppShell
            title="Cookie Shop"
            subtitle="Bánh Trung Thu Handmade"
            right={<CartButton />}
            transparent
            head={false}
        >
            {/* HERO + PRE-ORDER PICKER */}
            <section className="hero shell">
                <div className="hero-grid">
                <div className="hero-card">
                    <div className="hero-media">
                        <img
                            src={publicImg('/imgs/hop-4-banh.webp')}
                            alt="Hộp 4 bánh tự chọn vị"
                            loading="eager"
                            className="img-fade"
                            onLoad={(e) => e.currentTarget.classList.add('loaded')}
                        />
                        <div className="hero-scrim" />
                        <div className="hero-copy">
                            <span className="hero-pill">
                                <i className="fa-solid fa-moon" /> Mùa trăng 2026
                            </span>
                            <h1>
                                Trao hộp bánh,<br />
                                <em>gói trọn chân tình</em>
                            </h1>
                            <p>Bánh nướng &amp; bánh dẻo handmade, ít ngọt, giao đúng ngày bạn hẹn.</p>
                        </div>
                    </div>

                    <div className="hero-actions">
                        <button type="button" className="btn btn-primary btn-block" onClick={goMenuWithDate}>
                            Chọn bánh ngay
                            <i className="fa-solid fa-arrow-right" style={{ fontSize: 12 }} />
                        </button>
                        <Link to="/gift-box?box=box4" className="btn btn-quiet btn-block">
                            Tự ghép hộp quà
                        </Link>
                    </div>
                </div>

                <div className="pick-card card">
                    <div className="pick-head">
                        <div>
                            <div className="eyebrow">Lịch giao bánh Mùa Trăng 2026</div>
                            <div className="pick-title">Bạn muốn nhận bánh ngày nào?</div>
                        </div>
                        <span className="pick-note">
                            <i className="fa-regular fa-calendar-check" /> 3 đợt giao
                        </span>
                    </div>

                    <div style={{ padding: '0 14px' }}>
                        <div className="dates-2-grid">
                            {dates.map((d) => (
                                <button
                                    key={d.iso}
                                    type="button"
                                    className={`daychip-wide ${d.iso === selectedDate ? 'on' : ''}`}
                                    onClick={() => setSelectedDate(d.iso)}
                                >
                                    <div className="daychip-dow">{d.dowFull || (d.dow === 'T2' ? 'Thứ Hai' : 'Thứ Sáu')}</div>
                                    <div className="daychip-date">{d.day} Tháng 09</div>
                                    <div className="daychip-note">{d.iso === selectedDate ? 'Đang chọn' : d.note}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <button type="button" className="btn btn-soft btn-block btn-sm pick-cta" onClick={goMenuWithDate}>
                        Xem bánh giao ngày {selected?.day}.{selected?.month?.replace('Th ', '')}
                    </button>

                    <ul className="pick-trust">
                        <li><i className="fa-solid fa-check" /> Sên tay mỗi ngày, không chất bảo quản</li>
                        <li><i className="fa-solid fa-check" /> Miễn phí vỏ hộp &amp; túi xách khi đủ set</li>
                        <li><i className="fa-solid fa-check" /> Bếp ra lò giao chuẩn hẹn trong 3 đợt: 14/09, 18/09 &amp; 23/09</li>
                    </ul>
                </div>
                </div>
            </section>

            {/* QUICK BOXES */}
            <section className="shell">
                <Reveal className="section-head" as="div">
                    <h2 className="h-section">Set quà tự chọn</h2>
                    <Link to="/gift-box" className="link">Tất cả <i className="fa-solid fa-chevron-right" style={{ fontSize: 10 }} /></Link>
                </Reveal>

                <Reveal stagger className="box-tiles">
                    <Link to="/gift-box?box=single" className="box-tile">
                        <img src={publicImg('/imgs/banh-nuong-truyen-thong.webp')} alt="Mua lẻ 1 bánh" loading="lazy"
                            className="img-fade" onLoad={(e) => e.currentTarget.classList.add('loaded')} />
                        <div className="box-tile-body">
                            <div className="box-tile-name">Mua Lẻ 1 Cái</div>
                            <div className="box-tile-sub">Không cần hộp · Thưởng thức</div>
                            <div className="box-tile-price">Từ {formatCurrency(45000)}</div>
                        </div>
                    </Link>

                    <Link to="/gift-box?box=box2" className="box-tile">
                        <img src={publicImg('/imgs/hop-2-banh.webp')} alt="Hộp 2 bánh" loading="lazy"
                            className="img-fade" onLoad={(e) => e.currentTarget.classList.add('loaded')} />
                        <div className="box-tile-body">
                            <div className="box-tile-name">Hộp 2 Bánh</div>
                            <div className="box-tile-sub">Thanh lịch · gọn nhẹ</div>
                            <div className="box-tile-price">Từ {formatCurrency(90000)}</div>
                        </div>
                    </Link>

                    <Link to="/gift-box?box=box4" className="box-tile">
                        <span className="box-tile-tag">Phổ biến</span>
                        <img src={publicImg('/imgs/hop-4-banh.webp')} alt="Hộp 4 bánh" loading="lazy"
                            className="img-fade" onLoad={(e) => e.currentTarget.classList.add('loaded')} />
                        <div className="box-tile-body">
                            <div className="box-tile-name">Hộp 4 Bánh</div>
                            <div className="box-tile-sub">Kèm túi xách sen cá</div>
                            <div className="box-tile-price">Từ {formatCurrency(180000)}</div>
                        </div>
                    </Link>
                </Reveal>
            </section>

            {/* HOW IT WORKS — desktop */}
            <section className="shell desk-only">
                <div className="section-head">
                    <h2 className="h-section">Đặt bánh trong 3 bước</h2>
                    <span className="t-muted">Đặt trước tối thiểu 48 giờ để xưởng chuẩn bị nguyên liệu</span>
                </div>

                <Reveal stagger className="steps">
                    <div className="step">
                        <span className="step-no">01</span>
                        <div className="step-title">Chọn ngày nhận bánh</div>
                        <p>Chọn ngày và khung giờ bạn muốn nhận. Xưởng sắp lịch nướng theo đúng đơn của bạn.</p>
                    </div>
                    <div className="step">
                        <span className="step-no">02</span>
                        <div className="step-title">Ghép hộp theo sở thích</div>
                        <p>Tự chọn từng vị bánh, trọng lượng 150g hay 180g và tuỳ chọn trứng muối cho hộp 2 hoặc 4 bánh.</p>
                    </div>
                    <div className="step">
                        <span className="step-no">03</span>
                        <div className="step-title">Thanh toán VietQR</div>
                        <p>Quét mã trên app ngân hàng, hệ thống tự khớp đơn trong vài giây và giữ chỗ cho bạn.</p>
                    </div>
                </Reveal>
            </section>

            {/* FEATURED CAROUSEL */}
            <section className="shell">
                <div className="section-head">
                    <h2 className="h-section">Vị được yêu thích</h2>
                    <Link to="/menu" className="link">Xem menu <i className="fa-solid fa-chevron-right" style={{ fontSize: 10 }} /></Link>
                </div>

                <Carousel className="feat-row" bleed dots ariaLabel="Vị bánh được yêu thích">
                    {featuredCakes.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            className="feat-card"
                            onClick={() => navigate(`/gift-box?box=box4&cakeId=${c.id}`)}
                        >
                            <div className="feat-thumb">
                                <img src={publicImg(c.image)} alt={c.name} loading="lazy"
                                    className="img-fade" onLoad={(e) => e.currentTarget.classList.add('loaded')} />
                                {c.tag && <span className="feat-tag">{c.tag}</span>}
                            </div>
                            <div className="feat-body">
                                <div className="feat-kind">{c.kind}</div>
                                <div className="feat-name">{c.name}</div>
                                <div className="feat-desc clamp-2">{c.desc}</div>
                                <div className="feat-foot">
                                    <span className="t-price">{formatCurrency(c.price)}</span>
                                    <span className="feat-add"><i className="fa-solid fa-plus" /></span>
                                </div>
                            </div>
                        </button>
                    ))}
                </Carousel>
            </section>

            {/* PILLARS */}
            <section className="shell">
                <div className="section-head">
                    <h2 className="h-section">Vì sao chọn tiệm mình</h2>
                </div>

                <Reveal stagger className="pillars">
                    {PILLARS.map((p, i) => (
                        <div key={i} className="pillar">
                            <span className="pillar-icon"><i className={p.icon} /></span>
                            <div>
                                <div className="pillar-title">{p.title}</div>
                                <div className="pillar-body">{p.body}</div>
                            </div>
                        </div>
                    ))}
                </Reveal>
            </section>

            {/* SUPPORT */}
            <section className="shell">
                <Reveal className="support card">
                    <div className="support-top">
                        <img src={publicImg('/imgs/logo.png')} alt="Cookie Shop" className="brand-logo-circle" />
                        <div>
                            <div className="support-name">Cookie Shop</div>
                            <div className="support-sub">Bánh Trung Thu Handmade</div>
                        </div>
                    </div>

                    <div className="support-links">
                        <a href="tel:0856345789" className="support-btn">
                            <i className="fa-solid fa-phone" /> Gọi đặt bánh
                        </a>
                        <a href="https://zalo.me/0856345789" target="_blank" rel="noreferrer" className="support-btn">
                            <i className="fa-solid fa-comment-dots" /> Nhắn Zalo
                        </a>
                    </div>

                    <div className="support-cols desk-only">
                        <div>
                            <div className="support-col-title">Đặt bánh</div>
                            <Link to="/menu">Menu bánh</Link>
                            <Link to="/gift-box?box=box2">Hộp 2 bánh</Link>
                            <Link to="/gift-box?box=box4">Hộp 4 bánh</Link>
                            <Link to="/my-orders">Tra cứu đơn hàng</Link>
                        </div>
                        <div>
                            <div className="support-col-title">Thông tin</div>
                            <span>Bánh handmade, ít ngọt</span>
                            <span>Không chất bảo quản</span>
                            <span>Giao nội thành theo khung giờ</span>
                            <span>Freeship giao hàng mọi đơn</span>
                        </div>
                        <div>
                            <div className="support-col-title">Liên hệ</div>
                            <a href="tel:0856345789">Hotline: 0856.345.789</a>
                            <a href="https://zalo.me/0856345789" target="_blank" rel="noreferrer">Zalo: 0856.345.789</a>
                            <span>Nhận bánh 08:00 – 21:00</span>
                        </div>
                    </div>

                    <div className="support-note">
                        Giao 08:00 – 21:00 theo lịch hẹn · Thanh toán VietQR tự động
                    </div>
                </Reveal>
            </section>

            {/* FLOATING CHIP TO REOPEN PAUSE MODAL IF CLOSED */}
            {!orderEnabled && !showPauseModal && (
                <button
                    type="button"
                    className="home-paused-reopen-chip"
                    onClick={() => setShowPauseModal(true)}
                    title="Bấm để xem lại thông báo tạm dừng nhận đơn"
                >
                    <span className="paused-pulse-dot" style={{ width: 7, height: 7 }} />
                    <span>Tiệm tạm ngưng nhận đơn · <strong>Xem thông báo</strong></span>
                </button>
            )}

            {/* LARGE VISUAL ORDER PAUSE MODAL */}
            {!orderEnabled && showPauseModal && (
                <div className="home-paused-modal-overlay" onClick={() => setShowPauseModal(false)}>
                    <div className="home-paused-modal-card" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="home-paused-modal-close"
                            onClick={() => setShowPauseModal(false)}
                            aria-label="Đóng thông báo"
                        >
                            <i className="fa-solid fa-xmark" />
                        </button>

                        <div className="home-paused-hero-badge">
                            <span className="paused-pulse-dot" />
                            <span>THÔNG BÁO TẠM DỪNG NHẬN ĐƠN MỚI</span>
                        </div>

                        <div className="home-paused-icon-wrap">
                            <div className="home-paused-icon-glow" />
                            <div className="home-paused-icon-circle">
                                <i className="fa-solid fa-store-slash" />
                            </div>
                        </div>

                        <h2 className="home-paused-title">
                            {orderStatus.closedTitle || 'Tiệm Tạm Dừng Nhận Đơn Mới'}
                        </h2>

                        <p className="home-paused-desc">
                            {orderStatus.closedReason || 'Để chuẩn bị chu đáo và đảm bảo chất lượng từng mẻ bánh tươi ngon nhất phục vụ quý khách, tiệm xin phép tạm ngưng nhận thêm đơn mới vào lúc này. Rất mong quý khách thông cảm!'}
                        </p>

                        {orderStatus.reopenTime && (
                            <div className="home-paused-reopen-box">
                                <div className="home-paused-reopen-icon">
                                    <i className="fa-regular fa-clock" />
                                </div>
                                <div className="home-paused-reopen-text">
                                    <div className="home-paused-reopen-label">Thời gian dự kiến:</div>
                                    <div className="home-paused-reopen-val">{orderStatus.reopenTime}</div>
                                </div>
                            </div>
                        )}

                        <div className="home-paused-contact-card">
                            <div className="home-paused-contact-title">
                                Cần hỗ trợ khẩn cấp hoặc đặt đơn doanh nghiệp?
                            </div>
                            <div className="home-paused-contact-btns">
                                {orderStatus.hotline && (
                                    <a href={`tel:${orderStatus.hotline.replace(/\D/g, '')}`} className="home-paused-btn-call">
                                        <i className="fa-solid fa-phone" />
                                        <span>Hotline: {orderStatus.hotline}</span>
                                    </a>
                                )}
                                {orderStatus.zaloLink && (
                                    <a href={orderStatus.zaloLink} target="_blank" rel="noopener noreferrer" className="home-paused-btn-zalo">
                                        <i className="fa-solid fa-comment-dots" />
                                        <span>Nhắn Zalo tư vấn</span>
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="home-paused-foot-actions">
                            <Link
                                to="/menu"
                                className="btn btn-primary btn-block btn-lg"
                                onClick={() => setShowPauseModal(false)}
                            >
                                <i className="fa-solid fa-cookie-bite" style={{ marginRight: 8 }} />
                                Khám phá Menu &amp; Hương vị bánh
                            </Link>
                            <button
                                type="button"
                                className="home-paused-dismiss-link"
                                onClick={() => setShowPauseModal(false)}
                            >
                                Tiếp tục xem trang chủ
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}
